// Visible recording indicator + Stop control. Rendered in a shadow root so
// the customer site's CSS can never clobber it (and vice versa). Stop is a
// clean, participant-initiated stop -- it always sends /abort
// {reason:'participant_stopped'}; there is no screen-share fallback here
// (that only applies to the call page's handling of technical failures).

export interface ConsentDeps {
  onStop: () => void;
  doc?: Document;
}

export interface ConsentHandle {
  unmount: () => void;
}

const HOST_ATTR = 'data-userintuition-recorder-ui';

export function mountConsentUI(deps: ConsentDeps): ConsentHandle {
  const doc = deps.doc ?? document;

  const host = doc.createElement('div');
  host.setAttribute(HOST_ATTR, '');
  host.style.position = 'fixed';
  host.style.zIndex = '2147483647';
  host.style.bottom = '16px';
  host.style.right = '16px';
  host.style.left = 'auto';
  host.style.top = 'auto';

  const shadow = host.attachShadow({ mode: 'open' });
  shadow.innerHTML = `
    <style>
      :host { all: initial; }
      .uir-badge {
        display: flex;
        align-items: center;
        gap: 8px;
        font: 12px/1.4 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        background: rgba(20, 20, 20, 0.92);
        color: #fff;
        padding: 6px 10px;
        border-radius: 999px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
        pointer-events: auto;
      }
      .uir-dot {
        color: #ff4d4f;
        animation: uir-pulse 1.6s ease-in-out infinite;
      }
      @keyframes uir-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.35; }
      }
      .uir-stop {
        font: inherit;
        color: #fff;
        background: rgba(255, 255, 255, 0.14);
        border: none;
        border-radius: 999px;
        padding: 3px 10px;
        cursor: pointer;
      }
      .uir-stop:hover { background: rgba(255, 255, 255, 0.24); }
    </style>
    <div class="uir-badge">
      <span class="uir-dot" aria-hidden="true">&#9679;</span>
      <span>Recording for UserIntuition</span>
      <button class="uir-stop" type="button">Stop</button>
    </div>
  `;

  const stopButton = shadow.querySelector('.uir-stop');
  const onClick = () => deps.onStop();
  stopButton?.addEventListener('click', onClick);

  function attach() {
    (doc.body ?? doc.documentElement).appendChild(host);
  }
  if (doc.body) {
    attach();
  } else {
    doc.addEventListener('DOMContentLoaded', attach, { once: true });
  }

  return {
    unmount() {
      stopButton?.removeEventListener('click', onClick);
      host.remove();
    },
  };
}
